import { getBusinessDateString } from '@/utils/dateHelpers';

export interface CompanyLicensePublicationInput {
  state?: string | null;
  license_number?: string | null;
  expiration_date?: string | null;
}

export interface CompanyLicensePublicationState {
  status: 'public' | 'excluded';
  label: 'Public' | 'Excluded';
  reason: string;
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidIsoDate(value: string): boolean {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const parsed = new Date(0);
  parsed.setUTCHours(0, 0, 0, 0);
  parsed.setUTCFullYear(year, month - 1, day);

  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
}

export function getCompanyLicensePublicationState(
  license: CompanyLicensePublicationInput,
  businessDate: string = getBusinessDateString()
): CompanyLicensePublicationState {
  const state = license.state?.trim() ?? '';
  const licenseNumber = license.license_number?.trim() ?? '';
  const expirationDate = license.expiration_date?.trim() ?? '';

  if (!state) {
    return {
      status: 'excluded',
      label: 'Excluded',
      reason: 'Add a state abbreviation before publishing.'
    };
  }

  if (!licenseNumber) {
    return {
      status: 'excluded',
      label: 'Excluded',
      reason: 'Pending until an issued license number is entered.'
    };
  }

  if (!expirationDate) {
    return {
      status: 'excluded',
      label: 'Excluded',
      reason: 'Add an expiration date before publishing.'
    };
  }

  if (!isValidIsoDate(expirationDate)) {
    return {
      status: 'excluded',
      label: 'Excluded',
      reason: 'Enter a valid expiration date before publishing.'
    };
  }

  if (expirationDate < businessDate) {
    return {
      status: 'excluded',
      label: 'Excluded',
      reason: `Expired on ${expirationDate}.`
    };
  }

  return {
    status: 'public',
    label: 'Public',
    reason: `Published through ${expirationDate}.`
  };
}
